import { Controller } from "@hotwired/stimulus"

// Tracks how much of a text field is left, shows feedback as the user
// approaches the limit, highlights when they go over, and disables the
// surrounding form's submit buttons until they're back under.
//
// Usage:
//   data-controller="character-count"
//   data-character-count-max-value="4500"
//   ...with an input target and a feedback target inside.
//
// We deliberately do NOT truncate user input on paste — well-crafted
// products (Twitter, Bluesky, Slack, etc.) let users go over the limit
// and decide what to cut themselves. Server-side validation is the safety
// net for any submission that bypasses this controller.
export default class extends Controller {
    static targets = ["input", "feedback"]
    static values = {
        max: Number,
        threshold: { type: Number, default: 0.2 } // show feedback within 20% of limit by default
    }

    connect() {
        this.boundUpdateFeedback = this.updateFeedback.bind(this)
        this.inputTarget.addEventListener("input", this.boundUpdateFeedback)
        this.updateFeedback()
    }

    disconnect() {
        if (this.inputTarget) {
            this.inputTarget.removeEventListener("input", this.boundUpdateFeedback)
        }
        // Make sure we don't leave the form's submit button stuck disabled
        // if this controller goes away while over the limit.
        this.setOverLimit(false)
    }

    updateFeedback() {
        const max = this.maxValue
        const currentLength = this.effectiveLength(this.inputTarget.value)
        const remainingChars = max - currentLength
        const percentageRemaining = remainingChars / max
        const overLimit = currentLength > max

        this.feedbackTarget.textContent = overLimit
            ? `${this.pluralize(currentLength - max, "character")} over the ${this.formatNumber(max)} character limit`
            : `${this.pluralize(remainingChars, "character")} remaining`

        if (overLimit || percentageRemaining <= this.thresholdValue) {
            this.feedbackTarget.classList.remove("hidden")
        } else {
            this.feedbackTarget.classList.add("hidden")
        }

        this.feedbackTarget.classList.toggle("text-red-600", overLimit)
        this.feedbackTarget.classList.toggle("font-semibold", overLimit)

        this.setOverLimit(overLimit)
    }

    // Track over-limit state on the parent form so multiple character-count
    // controllers in the same form compose: the submit button stays disabled
    // as long as ANY field is over its limit.
    setOverLimit(overLimit) {
        const form = this.inputTarget.form
        if (!form) return

        if (!form._characterCountOverLimitFields) {
            form._characterCountOverLimitFields = new Set()
        }
        const overLimitFields = form._characterCountOverLimitFields

        if (overLimit) {
            overLimitFields.add(this.element)
        } else {
            overLimitFields.delete(this.element)
        }

        const shouldDisable = overLimitFields.size > 0
        form.querySelectorAll('[type="submit"]').forEach((button) => {
            button.disabled = shouldDisable
        })
    }

    // Form submissions encode newlines as \r\n, but textarea.value normalizes
    // them to \n. Each newline costs 2 chars over the wire — match that here
    // so this counter agrees with server-side String#length.
    effectiveLength(value) {
        const newlineCount = (value.match(/\n/g) || []).length
        return value.length + newlineCount
    }

    pluralize(count, word) {
        return `${this.formatNumber(count)} ${word}${count === 1 ? "" : "s"}`
    }

    formatNumber(number) {
        return number.toLocaleString()
    }
}
